import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { shortDate } from "@/lib/format";
import {
  UploadCloud, Users, Database, RefreshCw, Shield, Tag,
  Bell, FileClock, Lock, Globe, Search, Play, Mail, AlertTriangle, UserPlus,
} from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  return (
    <div className="space-y-6 animate-slide-up" data-testid="admin-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Admin Console</h1>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]">Admin only</span>
          </div>
          <p className="text-sm text-zinc-500 mt-1">Manage users, customer groups, rate visibility, notifications, and audit logs.</p>
        </div>
        <div className="text-xs text-zinc-500 text-right">
          <div className="data-label mb-0.5">Signed in as</div>
          <div className="metric text-zinc-900">{user?.email}</div>
        </div>
      </div>

      <Tabs defaultValue="upload">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 p-0 h-auto w-full justify-start flex-wrap">
          <TabsTrigger value="upload" data-testid="admin-tab-upload" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">Rate Sheet Upload</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">User Management</TabsTrigger>
          <TabsTrigger value="visibility" data-testid="admin-tab-visibility" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">Rate Visibility</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">Notifications</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="upload" className="mt-6"><RatesUploadPanel /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersPanel /></TabsContent>
        <TabsContent value="visibility" className="mt-6"><RateVisibilityPanel /></TabsContent>
        <TabsContent value="notifications" className="mt-6"><NotificationsPanel /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditLogPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------- Rate Upload -------------------- */
function RatesUploadPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    const r = await api.get("/admin/rates/upload-status", { withCredentials: true });
    setStatus(r.data);
  };
  useEffect(() => { refresh(); return () => clearInterval(pollRef.current); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/rates/upload", fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Upload received — importing in background");
      pollRef.current = setInterval(async () => { await refresh(); }, 3000);
      setTimeout(() => clearInterval(pollRef.current), 120000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const latest = status?.latest;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <div className="data-label flex items-center gap-2"><Database className="w-3 h-3 text-[#002FA7]" />Total Rate Rows</div>
          <div className="metric text-3xl mt-2">{(status?.total_rate_rows || 0).toLocaleString()}</div>
        </div>
        <div className="bg-white p-5">
          <div className="data-label">Latest Import</div>
          <div className="text-sm mt-2 metric">{latest?.filename || "—"}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{latest?.started_at ? shortDate(latest.started_at) : "Never"}</div>
        </div>
        <div className={`bg-white p-5 border-l-[3px] ${latest?.status === 'completed' ? 'border-l-[#16A34A]' : latest?.status === 'running' ? 'border-l-[#F59E0B]' : latest?.status === 'failed' ? 'border-l-[#DC2626]' : 'border-l-zinc-300'}`}>
          <div className="data-label">Status</div>
          <div className="metric text-lg mt-2 font-medium uppercase">{latest?.status || "idle"}</div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-8">
        <div className="text-center max-w-md mx-auto">
          <UploadCloud className="w-10 h-10 text-[#002FA7] mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold tracking-tight mb-2">Upload Quarterly FAK Rate Sheet</h3>
          <p className="text-xs text-zinc-500 mb-5">Drop the latest <code className="metric bg-zinc-100 px-1 border border-zinc-200">Main FAK Rate_NB_SB_AUS.xlsx</code>. Existing rates will be fully replaced with the new sheet's data.</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleUpload} data-testid="upload-input" />
          <Button data-testid="upload-btn" disabled={busy || latest?.status === 'running'} onClick={() => fileRef.current?.click()} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">
            {busy ? "Uploading..." : latest?.status === 'running' ? "Import in progress..." : "Choose .xlsx file"}
          </Button>
          <Button variant="ghost" size="sm" className="rounded-none ml-2" onClick={refresh}><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh status</Button>
        </div>

        {latest?.summary && (
          <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="upload-summary">
            <div className="data-label mb-3">Last import summary</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
              {Object.entries(latest.summary).map(([sheet, info]) => (
                <div key={sheet} className="bg-white p-4">
                  <div className="data-label">{sheet}</div>
                  <div className="metric text-2xl mt-1">{info.kept.toLocaleString()}</div>
                  <div className="text-[10px] text-zinc-400 mt-1 metric">of {info.scanned.toLocaleString()} scanned</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {latest?.error && (
          <div className="mt-6 bg-[#FEE2E2] border border-[#FCA5A5] p-4 text-sm text-[#991B1B]" data-testid="upload-error">
            {latest.error}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Users -------------------- */
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "USER", customer_group: "", send_email: false });
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    const [u, g] = await Promise.all([
      api.get("/admin/users", { withCredentials: true }),
      api.get("/admin/customer-groups", { withCredentials: true }),
    ]);
    setUsers(u.data);
    setGroups(g.data);
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!invite.email || invite.password.length < 8) {
      toast.error("Email and a password of at least 8 characters are required");
      return;
    }
    setInviting(true);
    try {
      const r = await api.post("/admin/users", {
        name: invite.name || null,
        email: invite.email,
        password: invite.password,
        role: invite.role,
        customer_group: invite.customer_group || null,
        send_email: invite.send_email,
      }, { withCredentials: true });
      const er = r.data?.email_result;
      if (invite.send_email) {
        if (er?.sent) toast.success(`User created — credentials emailed to ${invite.email}.`);
        else toast.warning(`User created, but the email could not be sent (${er?.error || er?.reason || "Resend not configured"}). Share the password manually.`);
      } else {
        toast.success(`User ${invite.email} created — share the password securely.`);
      }
      setInvite({ name: "", email: "", password: "", role: "USER", customer_group: "", send_email: false });
      load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create user");
    } finally {
      setInviting(false);
    }
  };

  const updateRole = async (userId, role) => {
    await api.patch(`/admin/users/${userId}/role`, { role }, { withCredentials: true });
    toast.success(`Role updated to ${role}`);
    load();
  };
  const updateGroup = async (userId, group) => {
    await api.patch(`/admin/users/${userId}/group`, { customer_group: group || null }, { withCredentials: true });
    toast.success(group ? `Group set to ${group}` : "Group cleared");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5"><div className="data-label flex items-center gap-2"><Users className="w-3 h-3 text-[#002FA7]" />Users</div><div className="metric text-3xl mt-2">{users.length}</div></div>
        <div className="bg-white p-5"><div className="data-label flex items-center gap-2"><Shield className="w-3 h-3 text-[#002FA7]" />Admins</div><div className="metric text-3xl mt-2">{users.filter(u => u.role === "ADMIN").length}</div></div>
        <div className="bg-white p-5"><div className="data-label flex items-center gap-2"><Tag className="w-3 h-3 text-[#002FA7]" />Customer Groups</div><div className="metric text-3xl mt-2">{groups.length}</div></div>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="invite-user-panel">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#002FA7]" />
          <h3 className="font-heading text-lg font-bold tracking-tight">Invite User</h3>
          <span className="text-xs text-zinc-500">Registration is invite-only — create accounts here.</span>
        </div>
        <form onSubmit={submitInvite} className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div className="lg:col-span-1">
            <Label className="data-label">Name</Label>
            <Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite(s => ({ ...s, name: e.target.value }))} placeholder="Jane Doe" className="rounded-none h-9 mt-1.5" />
          </div>
          <div className="lg:col-span-2">
            <Label className="data-label">Email *</Label>
            <Input data-testid="invite-email" type="email" required value={invite.email} onChange={(e) => setInvite(s => ({ ...s, email: e.target.value }))} placeholder="user@company.com" className="rounded-none h-9 mt-1.5" />
          </div>
          <div className="lg:col-span-1">
            <Label className="data-label">Password *</Label>
            <Input data-testid="invite-password" type="text" required minLength={8} value={invite.password} onChange={(e) => setInvite(s => ({ ...s, password: e.target.value }))} placeholder="min 8 chars" className="rounded-none h-9 mt-1.5" />
          </div>
          <div className="lg:col-span-1">
            <Label className="data-label">Role</Label>
            <Select value={invite.role} onValueChange={(v) => setInvite(s => ({ ...s, role: v }))}>
              <SelectTrigger data-testid="invite-role" className="rounded-none h-9 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{["USER","ADMIN"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-1">
            <Button data-testid="invite-submit" type="submit" disabled={inviting} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-9">{inviting ? "Creating..." : "Create User"}</Button>
          </div>
          <label className="lg:col-span-6 flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
            <input data-testid="invite-send-email" type="checkbox" checked={invite.send_email} onChange={(e) => setInvite(s => ({ ...s, send_email: e.target.checked }))} className="accent-[#002FA7] w-4 h-4" />
            <Mail className="w-3.5 h-3.5 text-[#002FA7]" />
            Email the login credentials to this user via Resend (instead of sharing manually).
          </label>
        </form>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading text-lg font-bold tracking-tight">All Users</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">{["User", "Email", "Role", "Customer Group", "Last Login"].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="users-tbody">
            {users.map(u => (
              <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    {u.picture ? <img src={u.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 bg-zinc-200 rounded-full flex items-center justify-center text-[10px] font-mono">{(u.name || u.email)[0]?.toUpperCase()}</div>}
                    <span className="font-medium">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 metric text-xs text-zinc-600">{u.email}</td>
                <td className="px-4 py-2.5">
                  <Select value={u.role || "USER"} onValueChange={(v) => updateRole(u.user_id, v)}>
                    <SelectTrigger data-testid={`role-${u.user_id}`} className="rounded-none h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">{["USER","ADMIN"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2.5">
                  <Input
                    data-testid={`group-${u.user_id}`}
                    defaultValue={u.customer_group || ""}
                    placeholder="(none)"
                    onBlur={(e) => { if ((e.target.value || "") !== (u.customer_group || "")) updateGroup(u.user_id, e.target.value); }}
                    className="rounded-none h-8 w-44 text-sm"
                  />
                </td>
                <td className="px-4 py-2.5 metric text-xs text-zinc-500">{u.last_login_at ? shortDate(u.last_login_at) : "—"}</td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-sm text-zinc-500">No users yet — first sign-in will appear here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- Rate Visibility -------------------- */
function RateVisibilityPanel() {
  const [filters, setFilters] = useState({ pol: "", pod: "", carrier: "", visibility: "ANY" });
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState({ rows: [], total: 0, shown: 0 });
  const [selected, setSelected] = useState(new Set());
  const [bulkVis, setBulkVis] = useState("PRIVATE");
  const [bulkGroup, setBulkGroup] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/customer-groups", { withCredentials: true }).then((r) => setGroups(r.data));
  }, []);

  const search = async () => {
    setBusy(true);
    setSelected(new Set());
    try {
      const params = {};
      if (filters.pol) params.pol = filters.pol;
      if (filters.pod) params.pod = filters.pod;
      if (filters.carrier) params.carrier = filters.carrier;
      if (filters.visibility && filters.visibility !== "ANY") params.visibility = filters.visibility;
      const r = await api.get("/admin/rates/search", { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const applyBulk = async () => {
    if (selected.size === 0) return toast.error("Select at least one rate");
    if (bulkVis === "PRIVATE" && !bulkGroup) return toast.error("Pick a customer group for PRIVATE rates");
    try {
      const r = await api.post("/admin/rates/bulk-visibility", {
        ids: [...selected], visibility: bulkVis, customer_group: bulkGroup || null,
      }, { withCredentials: true });
      toast.success(`Updated ${r.data.modified} rate(s)`);
      setSelected(new Set());
      search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const updateOne = async (id, visibility, group) => {
    try {
      await api.patch(`/admin/rates/${id}/visibility`, { visibility, customer_group: group || null }, { withCredentials: true });
      toast.success("Rate updated");
      search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-zinc-200 p-5">
        <div className="data-label mb-3 flex items-center gap-2"><Search className="w-3 h-3 text-[#002FA7]" />Filter rate rows</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label className="data-label">POL</Label><Input data-testid="rv-pol" value={filters.pol} onChange={(e) => setFilters({ ...filters, pol: e.target.value })} placeholder="SHANGHAI" className="rounded-none mt-1.5 h-10 metric uppercase" /></div>
          <div><Label className="data-label">POD</Label><Input data-testid="rv-pod" value={filters.pod} onChange={(e) => setFilters({ ...filters, pod: e.target.value })} placeholder="SYDNEY" className="rounded-none mt-1.5 h-10 metric uppercase" /></div>
          <div><Label className="data-label">Carrier</Label><Input data-testid="rv-carrier" value={filters.carrier} onChange={(e) => setFilters({ ...filters, carrier: e.target.value })} placeholder="MSC" className="rounded-none mt-1.5 h-10 metric uppercase" /></div>
          <div>
            <Label className="data-label">Visibility</Label>
            <Select value={filters.visibility} onValueChange={(v) => setFilters({ ...filters, visibility: v })}>
              <SelectTrigger data-testid="rv-vis-filter" className="rounded-none mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{["ANY", "PUBLIC", "PRIVATE"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="rv-search" disabled={busy} onClick={search} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10"><Search className="w-4 h-4 mr-2" />{busy ? "..." : "Search"}</Button>
          <div className="text-xs text-zinc-500 metric text-right">{data.shown} of {data.total.toLocaleString()} match</div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-[#FEF3C7] border border-[#FDE68A] p-4 flex items-center gap-3 flex-wrap" data-testid="rv-bulk-bar">
          <div className="text-sm font-medium text-[#92400E]">{selected.size} selected</div>
          <Select value={bulkVis} onValueChange={setBulkVis}>
            <SelectTrigger data-testid="rv-bulk-vis" className="rounded-none h-9 w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">{["PUBLIC", "PRIVATE"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
          {bulkVis === "PRIVATE" && (
            <Input data-testid="rv-bulk-group" value={bulkGroup} onChange={(e) => setBulkGroup(e.target.value)} placeholder="customer group" list="rv-groups" className="rounded-none h-9 w-48 bg-white" />
          )}
          <datalist id="rv-groups">{groups.map(g => <option key={g} value={g} />)}</datalist>
          <Button data-testid="rv-bulk-apply" onClick={applyBulk} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-9">Apply to {selected.size}</Button>
          <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-3 py-2.5 w-8"><input type="checkbox" data-testid="rv-select-all" checked={data.rows.length > 0 && selected.size === data.rows.length} onChange={(e) => setSelected(new Set(e.target.checked ? data.rows.map(r => r.id) : []))} /></th>
              {["Carrier", "Contract", "Lane", "Valid", "Sell (40HC)", "Visibility", "Group", "Actions"].map(h => <th key={h} className="px-3 py-2.5 data-label">{h}</th>)}
            </tr>
          </thead>
          <tbody data-testid="rv-tbody">
            {data.rows.map((r) => {
              const isPrivate = (r.visibility || "PUBLIC") === "PRIVATE";
              return (
                <tr key={r.id} className={`border-b border-zinc-100 hover:bg-zinc-50/60 ${isPrivate ? "bg-[#FEF3C7]/30" : ""}`} data-testid={`rv-row-${r.id}`}>
                  <td className="px-3 py-2.5"><input type="checkbox" data-testid={`rv-check-${r.id}`} checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} /></td>
                  <td className="px-3 py-2.5 font-heading font-semibold">{r.carrier}</td>
                  <td className="px-3 py-2.5 metric text-[11px] text-zinc-500">{r.contract_no || "—"}</td>
                  <td className="px-3 py-2.5 metric text-xs">{r.pol} → {r.pod}</td>
                  <td className="px-3 py-2.5 metric text-xs">{r.valid_from ? shortDate(r.valid_from) : "—"} → {r.valid_to ? shortDate(r.valid_to) : "—"}</td>
                  <td className="px-3 py-2.5 metric">{r.allin_sell_40hq != null ? `$${r.allin_sell_40hq.toLocaleString()}` : "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 inline-flex items-center gap-1 ${isPrivate ? "bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" : "bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0]"}`}>
                      {isPrivate ? <><Lock className="w-2.5 h-2.5" />Private</> : <><Globe className="w-2.5 h-2.5" />Public</>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 metric text-xs">{r.customer_group || "—"}</td>
                  <td className="px-3 py-2.5">
                    {isPrivate
                      ? <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" data-testid={`rv-make-public-${r.id}`} onClick={() => updateOne(r.id, "PUBLIC", null)}>Make Public</Button>
                      : <MakePrivateInline rateId={r.id} groups={groups} onApply={updateOne} />
                    }
                  </td>
                </tr>
              );
            })}
            {data.rows.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-sm text-zinc-500">Run a search above to list rate rows.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MakePrivateInline({ rateId, groups, onApply }) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState("");
  if (!open) {
    return <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" data-testid={`rv-make-private-${rateId}`} onClick={() => setOpen(true)}>Make Private…</Button>;
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input data-testid={`rv-group-${rateId}`} value={group} onChange={(e) => setGroup(e.target.value)} placeholder="group" list="rv-groups" className="rounded-none h-7 w-32 text-xs" />
      <Button size="sm" className="rounded-none h-7 text-xs bg-[#002FA7] hover:bg-[#002480]" data-testid={`rv-apply-${rateId}`} disabled={!group} onClick={() => { onApply(rateId, "PRIVATE", group); setOpen(false); }}>Apply</Button>
      <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs" onClick={() => setOpen(false)}>×</Button>
    </div>
  );
}

/* -------------------- Notifications -------------------- */
function NotificationsPanel() {
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, n, r] = await Promise.all([
      api.get("/admin/notifications/config", { withCredentials: true }),
      api.get("/admin/notifications", { withCredentials: true }),
      api.get("/admin/notifications/runs", { withCredentials: true }),
    ]);
    setConfig(c.data); setItems(n.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, []);

  const trigger = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/notifications/run-digest", {}, { withCredentials: true });
      toast.success(`Digest run — ${r.data.item_count} action(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const sevColor = (s) => s === "CRITICAL" ? "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" : s === "WARNING" ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : "bg-zinc-100 text-zinc-600 border-zinc-200";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className={`bg-white p-5 border-l-[3px] ${config?.resend_api_key_present ? "border-l-[#16A34A]" : "border-l-[#F59E0B]"}`}>
          <div className="data-label flex items-center gap-2"><Mail className="w-3 h-3 text-[#002FA7]" />Resend</div>
          <div className="metric text-lg mt-2 font-medium">{config?.resend_api_key_present ? "Connected" : "Not configured"}</div>
          <div className="text-[10px] text-zinc-500 mt-1 metric">{config?.resend_api_key_present ? `from ${config?.from_address}` : "Queue-only mode"}</div>
        </div>
        <div className="bg-white p-5"><div className="data-label">Recipient</div><div className="metric text-sm mt-2">{config?.recipient || "—"}</div></div>
        <div className="bg-white p-5"><div className="data-label flex items-center gap-2"><Bell className="w-3 h-3 text-[#002FA7]" />Queued items</div><div className="metric text-3xl mt-2">{items.filter(i => i.status === "queued").length}</div></div>
        <div className="bg-white p-5"><div className="data-label">Sent</div><div className="metric text-3xl mt-2">{items.filter(i => i.status === "sent").length}</div></div>
      </div>

      <div className="bg-white border border-zinc-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading text-lg font-bold tracking-tight">Run digest now</h3>
            <p className="text-xs text-zinc-500 mt-1">Scans bookings, shipments, and invoices for cut-off, free-time, and exception alerts.</p>
          </div>
          <Button data-testid="notif-run" disabled={busy} onClick={trigger} className="rounded-none bg-[#002FA7] hover:bg-[#002480]"><Play className="w-3.5 h-3.5 mr-2" />{busy ? "Running..." : "Run digest"}</Button>
        </div>
        {!config?.resend_api_key_present && (
          <div className="mt-3 bg-[#FEF3C7] border border-[#FDE68A] p-3 text-xs text-[#92400E] flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span><b>Queue-only mode.</b> Set <code className="metric">RESEND_API_KEY</code> in <code className="metric">backend/.env</code> to actually send emails. Items below are stored and visible here for review.</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-heading text-lg font-bold tracking-tight">Recent notifications</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">{["Severity", "Type", "Subject", "Ref", "Status", "Created"].map(h => <th key={h} className="px-3 py-2.5 data-label">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="notif-tbody">
            {items.map(i => (
              <tr key={i.id} className="border-b border-zinc-100">
                <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border ${sevColor(i.severity)}`}>{i.severity}</span></td>
                <td className="px-3 py-2.5 metric text-xs">{i.type}</td>
                <td className="px-3 py-2.5 text-xs">{i.subject}</td>
                <td className="px-3 py-2.5 metric text-[11px] text-zinc-500">{i.ref || "—"}</td>
                <td className="px-3 py-2.5 metric text-xs">{i.status}</td>
                <td className="px-3 py-2.5 metric text-[11px] text-zinc-500">{shortDate(i.created_at)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-zinc-500">No notifications yet — run a digest above.</td></tr>}
          </tbody>
        </table>
      </div>

      {runs.length > 0 && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-heading text-lg font-bold tracking-tight">Digest run history</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left">{["Executed", "Triggered by", "Items", "Resend", "Result"].map(h => <th key={h} className="px-3 py-2.5 data-label">{h}</th>)}</tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="px-3 py-2.5 metric text-xs">{shortDate(r.executed_at)}</td>
                  <td className="px-3 py-2.5 metric text-xs">{r.triggered_by}</td>
                  <td className="px-3 py-2.5 metric">{r.item_count}</td>
                  <td className="px-3 py-2.5 metric text-xs">{r.resend_configured ? "configured" : "queue-only"}</td>
                  <td className="px-3 py-2.5 metric text-[11px] text-zinc-500">{r.send_result?.sent ? "sent" : r.send_result?.queued ? "queued" : (r.send_result?.error || "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------- Audit Log -------------------- */
function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ action: "", actor: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.actor) params.actor = filters.actor;
      const r = await api.get("/admin/audit-logs", { params, withCredentials: true });
      setRows(r.data);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <div className="bg-white border border-zinc-200 p-5">
        <div className="data-label mb-3 flex items-center gap-2"><FileClock className="w-3 h-3 text-[#002FA7]" />Filter</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="data-label">Action contains</Label><Input data-testid="al-action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} placeholder="rate.visibility" className="rounded-none mt-1.5 h-10 metric" /></div>
          <div><Label className="data-label">Actor email</Label><Input data-testid="al-actor" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} placeholder="@example.com" className="rounded-none mt-1.5 h-10 metric" /></div>
          <Button data-testid="al-search" disabled={busy} onClick={load} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10"><Search className="w-4 h-4 mr-2" />Refresh</Button>
          <div className="text-xs text-zinc-500 metric text-right">{rows.length} entries</div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">{["Timestamp", "Actor", "Action", "Target", "Before → After"].map(h => <th key={h} className="px-3 py-2.5 data-label">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="al-tbody">
            {rows.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60 align-top">
                <td className="px-3 py-2.5 metric text-[11px] text-zinc-500 whitespace-nowrap">{shortDate(r.created_at)}</td>
                <td className="px-3 py-2.5 text-xs">
                  <div className="font-medium">{r.actor_email || "—"}</div>
                  <div className="metric text-[10px] text-zinc-400">{r.actor_role}</div>
                </td>
                <td className="px-3 py-2.5 metric text-xs"><span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200">{r.action}</span></td>
                <td className="px-3 py-2.5 text-xs">
                  <div className="metric text-zinc-600">{r.target_type || "—"}</div>
                  <div className="metric text-[10px] text-zinc-400 truncate max-w-[180px]" title={r.target_id || ""}>{r.target_id || ""}</div>
                </td>
                <td className="px-3 py-2.5 text-[11px]">
                  {r.before && <div className="metric text-zinc-500 line-through truncate max-w-md" title={JSON.stringify(r.before)}>{JSON.stringify(r.before)}</div>}
                  {r.after && <div className="metric text-[#166534] truncate max-w-md" title={JSON.stringify(r.after)}>{JSON.stringify(r.after)}</div>}
                  {r.metadata && Object.keys(r.metadata).length > 0 && <div className="metric text-zinc-400 truncate max-w-md" title={JSON.stringify(r.metadata)}>{JSON.stringify(r.metadata)}</div>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-sm text-zinc-500">No audit events yet. Actions like role/group changes, rate uploads, and visibility flips are recorded here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
